/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useCallback } from 'preact/hooks';
import { useState } from 'preact/hooks';
import { Dropdown } from '#UNSAFE_Dropdown';
import { PositionData } from '../UNSAFE_Floating';

type DropdownProps = ComponentProps<typeof Dropdown>;
type Props = Pick<
  DropdownProps,
  | 'anchorRef'
  | 'children'
  | 'flipOptions'
  | 'id'
  | 'initialFocus'
  | 'isOpen'
  | 'minWidth'
  | 'offset'
  | 'onClose'
  | 'onPosition'
  | 'sizeOptions'
>;

const DEFAULT_PLACEMENT = 'bottom-start';

/**
 * StickyPositioningDropdown is used to display content which supports conditional rendering,
 * or other cases where the size of the content can change and cause the dropdown to rerender
 * in a different placement. This component ensures the repositioning happens only once,
 * until there is a collision, instead of allowing the dropdown to keep trying to
 * move back to its default position on every render.
 */
const StickyPositioningDropdown = ({ children, isOpen, onPosition, ...rest }: Props) => {
  const [placement, setPlacement] = useState<DropdownProps['placement']>(DEFAULT_PLACEMENT);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);

  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);

    // reset the placement to the default when the dropdown closes, not opens, so that it doesn't
    // initially open in the previous position and then move to the default position on the
    // rerender after the state is updated
    if (!isOpen) {
      setPlacement(DEFAULT_PLACEMENT);
    }
  }
  const handlePosition = useCallback(
    (positionData: PositionData) => {
      // update the placement so that the dropdown stays where it is until there is a collision,
      // i.e. it no longer fits, instead of letting it try to move back to the default position
      // on every render
      setPlacement(positionData.placement);

      // call the callback that was passed in through props
      onPosition?.(positionData);
    },
    [onPosition, setPlacement]
  );

  return (
    <Dropdown isOpen={isOpen} onPosition={handlePosition} placement={placement} {...rest}>
      {children}
    </Dropdown>
  );
};

export { StickyPositioningDropdown };
